import React, { useEffect, useMemo, useState } from 'react';
import KaTeXContent from './KaTeXContent';
import { parseQuestionNumber, getResultType, renderModalKatex } from './analysisModalUtils';

/**
 * Shared "Compare Answers" dialog. Renders the question-by-question comparison
 * table (student vs correct answer) for a completed task. Extracted from the
 * coaching task card so the same dialog can be reused elsewhere (e.g. the View
 * Syllabus modal).
 *
 * Props:
 *  - comparisonData: response from GET /tasks/{id}/comparison
 *      (expects `.comparison_data` and optionally `.evaluation`)
 *  - onClose(): close the dialog
 *  - onExplainConcept(question): optional — enables the "Ask Eddy" action
 *  - onEdit(evaluation): optional — enables the default footer "Edit" action
 *  - onPersonalize(evaluation): optional — enables "Personalized Sheet"
 *  - title: optional header title (default "Compare Answers")
 *  - studentAnswerLabel: optional column header for the student's answer
 *  - footer: optional node that fully replaces the default footer
 *  - emptyState: optional node shown when there is no question analysis
 */
const CompareAnswersModal = ({
  comparisonData,
  onClose,
  onExplainConcept,
  onEdit,
  onPersonalize,
  title = 'Compare Answers',
  studentAnswerLabel = 'Student Answer',
  footer = null,
  emptyState = null,
}) => {
  const [resultFilter, setResultFilter] = useState('all');
  const [flashcard, setFlashcard] = useState(null);

  useEffect(() => {
    if (comparisonData) renderModalKatex();
  }, [comparisonData]);

  const questionAnalysis = useMemo(() => {
    const source = comparisonData?.comparison_data?.question_analysis || [];
    return [...source]
      .sort((a, b) => parseQuestionNumber(a?.question_number, 0) - parseQuestionNumber(b?.question_number, 0))
      .filter((analysis) => resultFilter === 'all' || getResultType(analysis) === resultFilter);
  }, [comparisonData, resultFilter]);

  const totalMarksAwarded = useMemo(
    () => questionAnalysis.reduce((sum, qa) => sum + (Number(qa.marks_awarded) || 0), 0),
    [questionAnalysis]
  );

  const totalMaxMarks = useMemo(
    () => questionAnalysis.reduce((sum, qa) => sum + (Number(qa.max_marks) || 1), 0),
    [questionAnalysis]
  );

  if (!comparisonData) return null;

  const openFlashcard = (question, fallbackIndex = 0) => {
    setFlashcard({
      ...question,
      question_number: question?.question_number || parseQuestionNumber(question?.question_number, fallbackIndex),
    });
  };

  const hasQuestions =
    comparisonData.comparison_data?.question_analysis &&
    comparisonData.comparison_data.question_analysis.length > 0;
  const showDefaultFooter = comparisonData.evaluation && (onEdit || onPersonalize);

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="sticky top-0 z-10 p-4 sm:p-6 bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 border-b border-gray-200 shadow">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-white">{title}</h2>
                <p className="text-sm text-white/80 mt-1">
                  Score: {comparisonData.comparison_data?.overall_score || 0}% • Grade: {comparisonData.comparison_data?.grade || 'N/A'}
                </p>
              </div>
              <button
                onClick={onClose}
                aria-label="Close"
                className="text-white/80 hover:text-white text-2xl sm:text-3xl font-bold w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full hover:bg-white/15 transition-all"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {hasQuestions ? (
              <div>
                <div className="mb-4 flex items-center justify-end gap-2">
                  <label htmlFor="comparison-result-filter" className="text-sm text-gray-700">Show:</label>
                  <select
                    id="comparison-result-filter"
                    value={resultFilter}
                    onChange={(e) => setResultFilter(e.target.value)}
                    className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="all">All Answers</option>
                    <option value="correct">Correct Only</option>
                    <option value="partial">Partially Correct Only</option>
                    <option value="incorrect">Incorrect Only</option>
                  </select>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-300"><strong>Q. No.</strong></th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-300"><strong>Question</strong></th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-300"><strong>{studentAnswerLabel}</strong></th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-300"><strong>Correct Answer</strong></th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-300"><strong>Marks</strong></th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-300"><strong>Result</strong></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {questionAnalysis.map((analysis, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-4 text-sm font-medium text-gray-900 border-b border-gray-200">
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={() => openFlashcard(analysis, index)}
                                className="text-xs px-2 py-1 bg-yellow-100 text-blue-700 rounded hover:bg-yellow-200"
                                title="Explain this concept"
                              >
                                ℹ️
                              </button>
                              <span>{parseQuestionNumber(analysis.question_number, index)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700 border-b border-gray-200 max-w-xs">
                            <div className="truncate" title={analysis.question_text || analysis.question || 'Question not available'}>
                              <KaTeXContent content={analysis.question_text || analysis.question || 'Question not available'} className="inline" />
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700 border-b border-gray-200 max-w-xs">
                            <div className="flex items-center space-x-2">
                              <div className="truncate" title={analysis.student_answer || 'No answer provided'}>
                                <KaTeXContent content={analysis.student_answer || 'No answer provided'} className="inline" />
                              </div>
                              {analysis.hint_used && (
                                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded whitespace-nowrap">
                                  💡 Hint
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700 border-b border-gray-200 max-w-xs">
                            <div className="truncate" title={analysis.correct_answer || 'Answer not available'}>
                              <KaTeXContent content={analysis.correct_answer || 'Answer not available'} className="inline" />
                            </div>
                          </td>
                          <td className="px-4 py-4 text-center border-b border-gray-200">
                            <div className="text-sm font-medium text-blue-700">
                              {analysis.marks_awarded?.toFixed(1) || 0} / {analysis.max_marks || 1}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-center border-b border-gray-200">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              analysis.is_correct === true || analysis.is_correct === 'correct'
                                ? 'bg-green-100 text-green-800'
                                : analysis.partial_credit > 0
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {analysis.is_correct === true || analysis.is_correct === 'correct' ? '✓ Correct' : analysis.partial_credit > 0 ? '~ Partial' : '✗ Incorrect'}
                            </span>
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-blue-50 font-semibold">
                        <td colSpan="3" className="px-4 py-4 text-right font-bold text-gray-900 border-b border-gray-200">
                          Total Marks:
                        </td>
                        <td className="px-4 py-4 text-center border-b border-gray-200">
                          <div className="text-lg font-bold text-blue-700">
                            {totalMarksAwarded.toFixed(1)} / {totalMaxMarks}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center border-b border-gray-200">
                          <span className="text-lg font-bold text-gray-700">
                            {totalMaxMarks > 0 ? Math.round((totalMarksAwarded / totalMaxMarks) * 100) : 0}%
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              emptyState || (
                <div className="text-center py-8 text-gray-500">
                  <p>No comparison data available</p>
                </div>
              )
            )}
          </div>

          {footer ? (
            footer
          ) : showDefaultFooter && (
            <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex gap-3 justify-end">
                {onEdit && (
                  <button
                    onClick={() => onEdit(comparisonData.evaluation)}
                    className="px-4 py-2 border-2 bg-orange-600 text-white border-orange-650 hover:bg-orange-700 rounded-lg font-medium flex items-center text-sm"
                  >
                    ✏️ Edit
                  </button>
                )}
                {onPersonalize && (
                  <button
                    onClick={() => onPersonalize(comparisonData.evaluation)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center text-sm"
                  >
                    🎯 Personalized Sheet
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {flashcard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-gray-200 bg-indigo-50">
              <h3 className="text-lg font-semibold text-indigo-900">
                Question {flashcard.question_number}
              </h3>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto text-sm text-gray-800">
              <p><strong>Question:</strong>{' '}<KaTeXContent content={flashcard.question_text || flashcard.question || 'Question not available'} className="inline" /></p>
              <p><strong>Student Answer:</strong>{' '}<KaTeXContent content={flashcard.student_answer || 'No answer provided'} className="inline" /></p>
              <p><strong>Correct Answer:</strong>{' '}<KaTeXContent content={flashcard.correct_answer || 'Answer not available'} className="inline" /></p>
              <p><strong>Feedback:</strong>{' '}<KaTeXContent content={flashcard.feedback || 'Feedback not available'} className="inline" /></p>
              {(flashcard.answer_explanation || flashcard.explanation) && (
                <p>
                  <strong>Explanation:</strong>{' '}
                  <KaTeXContent
                    content={flashcard.answer_explanation || flashcard.explanation}
                    className="inline"
                  />
                </p>
              )}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
              {onExplainConcept && (
                <button
                  type="button"
                  onClick={() => {
                    onExplainConcept(flashcard);
                    setFlashcard(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                >
                  ℹ️ Ask Eddy
                </button>
              )}
              <button
                type="button"
                onClick={() => setFlashcard(null)}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-md hover:bg-orange-600"
              >
                Ok
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CompareAnswersModal;
